import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useHistory } from "react-router-dom";
import { SvgIcon } from "@progress/kendo-react-common";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { updateCurrUser } from "../../actions/auth";
import { setFileMetaData, setSelectedContentItem } from "../../actions/contentManager";
import { setSelectedView } from "../../actions/task";
import { icon, label, notifyIcon, number, quote, route } from "../../config";
import { getProjectUrl } from "../../helper/common";
import { getContentAttachmentById } from "../../shared/services/contentManger.services";
import { createDynamicTeamName, getNotification, isTrueBit } from "../../utils/common";
import { GridContext } from "../../utils/kendo";
import {
  convertExtensionToIcon,
  handleLibraryAttachmentClick,
  handleMarkAsFavorite,
  mapData,
  useFolderHelpers,
} from "../ContentManagementSystem/helper";
import { getTaskDetails, openSidebar } from "../Tasks/tasks.service";
import BookmarkPopup from "./BookmarkPopup";
import { isEntityTypeProject, isEntityTypeTask } from "./bookmark.helper";
import "./bookmarks.scss";

/**
 * MarkBookmark Component
 * Implements removing and drag-drop functionality for bookmarks
 * Parent Component : BookmarksList
 * @param {Object} props - Component properties
 * @author Dimple Sahota
 */
const MarkBookmark = (props) => {
  const { type } = props;
  const history = useHistory();
  const currentContext = React.useContext(GridContext);
  const { id, operationalTeamName } = useSelector((state) => state.auth.user);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [popupItem, setPopupItem] = useState(null);
  const [hoveredBookmark, setHoveredBookmark] = useState(null);
  const [showPopupAnimation, setShowPopupAnimation] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { contentManagerLibraryPath } = useSelector((state) => state.contentManager);
  const allProjectsList = useSelector((state) => state.tasks.defaultDetails.allProjectsList);
  const { fetchAndSetFolder } = useFolderHelpers();
  const dispatch = useDispatch();
  const popupTimeoutRef = React.useRef(null);
  const isOverPopupRef = React.useRef(false);
  const isOverItemRef = React.useRef(false);
  const hoverContainerRef = React.useRef(null);

  // Trigger animation when popup becomes visible
  useEffect(() => {
    if (popupItem) {
      const timer = setTimeout(() => {
        setShowPopupAnimation(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setShowPopupAnimation(false);
    }
  }, [popupItem]);

  let pathname = location.pathname;
  const { params } = matchPath(pathname, {
    path: [route.PRIVATE_ROUTE.CMS.ROUTER_PATH],
  }) || { params: {} };
  let projectId = params?.projectId || null;

  /** Cleanup timeout on unmount
   * @author Muskan Thakur
   * */
  useEffect(() => {
    return () => {
      if (popupTimeoutRef.current) {
        clearTimeout(popupTimeoutRef.current);
      }
    };
  }, []);

  const handleLibraryFilesView = (selectedItem) => {
    dispatch(setFileMetaData({}));
    dispatch(setSelectedContentItem([]));
    history.push(`${route.PRIVATE_ROUTE.Y_DOC.PATH}/${selectedItem?.id}`);
    setTimeout(() => {
      history.go(0);
    }, 50);
  };

  /**
   *  handles bookmark click
   *  sidebar is opended if entity type is tasks and queueTasks page is opened if entity type is queue
   * @params {Object}
   */
  const handleBookmarkClick = async (e) => {
    e.stopPropagation();
    props?.setType(null);
    if (isEntityTypeTask(props)) {
      try {
        const taskData = await dispatch(
          getTaskDetails({
            taskId: props.dataItem?.Id,
            userId: id,
            taskHistoryId: null,
          })
        );

        if (taskData?.taskDetails) {
          if (isTrueBit(taskData.taskDetails, "IsDeleted") && !taskData?.hasAccess) {
            getNotification(quote.TASK_ACCESS_DENIED, notifyIcon.WARNING_ICON);
            return;
          }
        }

        openSidebar({ id: props.dataItem?.Id, userId: id, taskHistoryId: null }, null, true);
      } catch (error) {
        openSidebar({ id: props.dataItem?.Id, userId: id, taskHistoryId: null }, null, true);
      }
    } else if (type === label.LIBRARY) {
      if (props.dataItem?.Type === label.FILE) {
        const attachmentDetails = props?.dataItem?.attachmentDetails;
        handleLibraryFilesView(attachmentDetails);
      } else {
        handleLibraryAttachmentClick(props.dataItem?.Id, history, fetchAndSetFolder);
      }
    } else {
      history.push(route.PRIVATE_ROUTE.QUEUE_TASKS.PATH + "/" + props.dataItem?.Id + "/" + encodeURIComponent(props.dataItem.Name));
    }
  };

  /**
   * sets the selected view and navigates to project with access verification
   */
  const handleProjectBookMarkClick = async () => {
    await dispatch(updateCurrUser({ lastSelectedBoard: label.GRID }));
    await dispatch(updateCurrUser({ mode: number.ZERO }));
    dispatch(setSelectedView(label.LIST_VIEW));
    props?.setType(null);

    const project = allProjectsList?.find((p) => p.value == props.dataItem?.Id);

    if (project && project.QueueId && !(project.owners + "," + project.collaborators).includes(id)) {
      getNotification(createDynamicTeamName(quote.PROJECT_ACCESS_DENIED, operationalTeamName), notifyIcon.WARNING_ICON);
      return;
    }

    const projectUrl = getProjectUrl({ projectId: props.dataItem?.Id, projectName: props.dataItem?.Name });
    history.push(projectUrl);
  };

  /**
   *  handles truncating bookmark text
   * @params {String}
   * @author Sejal
   */
  const truncateBookmark = (str, textType) => {
    const isLibraryEntity = props?.dataItem?.EntityType === label.LIBRARY;
    const limit =
      hoveredItem && textType === label.PROJECT_NAME && (isLibraryEntity || type === label.QUEUE_ENTITY)
        ? number.SIXTEEN
        : !hoveredItem && textType === label.PROJECT_NAME && (isLibraryEntity || type === label.QUEUE_ENTITY)
        ? number.TWENTY_THREE
        : number.TWENTY_EIGHT;
    return str.length > limit ? str.substring(number.ZERO, limit) + "..." : str;
  };

  /**
   * Clears hover timeout if exists
   */
  const clearHoverTimeout = () => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
  };

  /**
   * Closes hover state and popup
   */
  const closeHoverState = () => {
    if (!isOverPopupRef.current && !isOverItemRef.current) {
      setHoveredBookmark(null);
      setPopupItem(null);
      setHoveredItem(null);
    }
  };

  /**
   * Handles bookmark hover enter event for the entire item container
   * Prevents flickering by using ref tracking and suppressing during drag
   * @param {String} itemId - Bookmark item ID
   */
  const handleOnMouseEnter = (itemId) => {
    // Don't show popup during drag operations
    if (isDragging) return;

    isOverItemRef.current = true;
    clearHoverTimeout();
    setHoveredItem(itemId);
    setPopupItem(itemId);
  };

  /**
   * Handles bookmark hover leave event
   * Uses debounced timeout to prevent flickering during mouse movement within item
   * Increased timeout to allow time to move to popup across the gap
   * @author Muskan Thakur
   */
  const handleOnMouseLeave = () => {
    isOverItemRef.current = false;
    clearHoverTimeout();
    popupTimeoutRef.current = setTimeout(closeHoverState, 600);
  };

  /**
   * Handles bookmark icon hover enter event
   * Maintains hover state when moving from item to bookmark icon
   * @param {String} itemId - Bookmark item ID
   */
  const handleBookmarkOnMouseEnter = (itemId) => {
    if (isDragging) return;

    isOverItemRef.current = true;
    clearHoverTimeout();
    setHoveredItem(itemId);
    setHoveredBookmark(itemId);
    // Keep existing popup state if already set
    if (!popupItem) {
      setPopupItem(itemId);
    }
  };

  /**
   * Handles bookmark icon hover leave event
   * Uses debounced timeout to prevent flickering
   * @author Muskan Thakur
   */
  const handleBookmarkOnMouseLeave = () => {
    clearHoverTimeout();
  };

  /**
   * Handles bookmark icon click for removing bookmark
   * @param {Object} e - Event object
   * @author Dimple Sahota
   */
  const handleBookmarkIconClick = async (e) => {
    e.stopPropagation(); // Prevents any unwanted parent event propagation
    const isLibrary = props?.dataItem?.EntityType === label.LIBRARY;
    if (props.handleRemoveFavorite) {
      props.handleRemoveFavorite(props.dataItem); // Calls handleOnDrop from the parent component
      if (isLibrary) {
        if (pathname.includes(route.PRIVATE_ROUTE.CMS.PATH) && props.dataItem?.ProjectId == projectId) {
          const attachmentData = await getContentAttachmentById(props.dataItem?.Id);
          const { item } = attachmentData;

          const project = item?.projectId ? allProjectsList?.find((proj) => proj?.ProjectId === item?.projectId) : null;
          const ProjectName = project?.ProjectName || "";
          const libraryName = item?.projectId
            ? `${contentManagerLibraryPath}${route.PRIVATE_ROUTE.CMS.PATH}`
            : `${contentManagerLibraryPath}/${route.PRIVATE_ROUTE.CMS.PATH}`;
          const [mappedRestoredItem] = mapData([item], libraryName);
          await handleMarkAsFavorite(mappedRestoredItem, mappedRestoredItem?.projectId);
        }
      }
    }
  };

  /**
   * sets the selected view ProjectCode Grid
   */
  const handleCodeOnClick = async (e) => {
    props?.setType(null);
    if (isEntityTypeProject(props)) {
      e.stopPropagation();

      const project = allProjectsList?.find((p) => p.value == props.dataItem?.Id);

      if (project && project.QueueId && !(project.owners + "," + project.collaborators).includes(id)) {
        getNotification(createDynamicTeamName(quote.PROJECT_ACCESS_DENIED, operationalTeamName), notifyIcon.WARNING_ICON);
        return;
      }

      history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${props.dataItem.Id}`);
    }
    await dispatch(updateCurrUser({ lastSelectedBoard: label.GRID }));
  };

  /**
   * Returns a truncated version of the appropriate bookmark value based on entity type.
   * If the entity is a Task, it uses `ProjectTaskID`.
   * If the entity is a Project, it uses `ProjectCode`.
   * Otherwise, it defaults to `Name`.
   *
   * @param {Object} props.dataItem - The data item containing entity details.
   * @param {string} [props.dataItem.ProjectTaskID] - The Task ID (if the entity is a Task).
   * @param {string} [props.dataItem.ProjectCode] - The Project Code (if the entity is a Project).
   * @param {string} [props.dataItem.Name] - The default name (if neither Task nor Project).
   * @returns {string} - The truncated bookmark value.
   */
  const getTruncatedBookmark = (props) => {
    if (isEntityTypeTask(props)) {
      return truncateBookmark(props?.dataItem?.ProjectTaskID, label.PROJECT_CODE);
    } else if (isEntityTypeProject(props)) {
      return truncateBookmark(props?.dataItem?.ProjectCode, label.PROJECT_CODE);
    } else {
      return truncateBookmark(props?.dataItem?.Name, label.PROJECT_NAME);
    }
  };

  /**
   * Handles popup mouse enter event
   * Clears any pending close timeout when mouse enters popup
   * @author Muskan Thakur
   */
  const handlePopupMouseEnter = () => {
    isOverPopupRef.current = true;
    clearHoverTimeout();
  };

  /**
   * Handles popup mouse leave event
   * Uses debounced close to allow returning to item
   * Increased timeout to allow time to move back to item across the gap
   * @author Muskan Thakur
   */
  const handlePopupMouseLeave = () => {
    isOverPopupRef.current = false;
    clearHoverTimeout();
    popupTimeoutRef.current = setTimeout(closeHoverState, 600);
  };

  /**
   * Renders a bookmark item with interactive elements such as remove bookmark, drag & drop,
   * tooltips, and a popup menu for additional actions.
   *
   *
   * @returns {JSX.Element} The bookmark component.
   * @author Dimple Sahota
   */
  const bookmarkJsx = () => {
    const isLibrary = props?.dataItem?.EntityType === label.LIBRARY;
    const isProject = props?.dataItem?.EntityType === label.PROJECT;
    const isHovered = hoveredItem === props?.dataItem?.Id && !isDragging;
    const isBookmarkableType = type !== label.QUEUE_ENTITY && type !== label.LIBRARY;
    const iconData = isLibrary ? convertExtensionToIcon(props?.dataItem?.attachmentDetails) : null;
    const elementId = isProject ? `bookmark-project-${props?.dataItem?.Id}` : `bookmark-task-${props?.dataItem?.Id}`;
    const hasOpenPopup = popupItem === props?.dataItem?.Id && !isDragging;

    return (
      <div
        ref={hoverContainerRef}
        className={`position-relative p-2 bookmark-hover-container ${hasOpenPopup ? "bookmark-item-with-popup ps-3" : ""}`}
        id={elementId}
        draggable={true}
        onMouseEnter={() => handleOnMouseEnter(props?.dataItem.Id)}
        onMouseLeave={handleOnMouseLeave}>
        {/* Favorite Item Project Code */}
        <div className='text-left' onClick={handleCodeOnClick}>
          {isHovered && (
            <span className='drag-icon cursor-move' onMouseDown={(e) => e.stopPropagation()}>
              {icon.DRAG_INDICATOR}
            </span>
          )}
          {/* Favorite Item attachment icon */}
          {isHovered && isLibrary && iconData?.svgIcon?.content && <SvgIcon icon={iconData?.svgIcon} size='large' className='file-icon-size' />}

          <span className='bookmark-text'>{getTruncatedBookmark(props)}</span>
          {/* Favorite Item Project Name */}
          <div >
            {isBookmarkableType && (
              <span className='bookmark-name-text'>
                {isEntityTypeTask(props)
                  ? truncateBookmark(props?.dataItem?.Name, label.PROJECT_NAME)
                  : truncateBookmark(props?.dataItem?.Name, label.PROJECT_NAME)}
              </span>
            )}
          </div>
          {/* Favorite Icon */}
          {isHovered && (
            <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
              <span
                className='bookmark-icon-popup position-absolute'
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  handleBookmarkOnMouseEnter(props?.dataItem?.Id);
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation();
                  handleBookmarkOnMouseLeave();
                }}
                onClick={handleBookmarkIconClick}>
                {hoveredBookmark === props?.dataItem?.Id ? (
                  <span>{icon.BOOKMARK_FAVORITE}</span>
                ) : (
                  <span title='Remove Favorite'>{icon.BOOKMARK_MARK_FAVORITE}</span>
                )}
              </span>
            </Tooltip>
          )}
        </div>
        {/* Bookmark Popup */}
        {hasOpenPopup && (type === label.PROJECT || type === label.QUEUE_ENTITY) && props?.dataItem?.EntityType !== label.BREADCRUMB_TASKS && (
          <BookmarkPopup
            type={type}
            popupItem={popupItem}
            setType={props?.setType}
            dataItem={props?.dataItem}
            setHoveredItem={setHoveredItem}
            setHoveredBookmark={setHoveredBookmark}
            onPopupMouseEnter={handlePopupMouseEnter}
            onPopupMouseLeave={handlePopupMouseLeave}
            showAnimation={showPopupAnimation}
          />
        )}
      </div>
    );
  };

  /**
   * Handles onDragOver event
   * @param {Object} e
   * @returns {Void}
   * @author Himanshu Negi
   */
  const onDragOverHandler = (e) => {
    if (!e) return;
    e.preventDefault();
    if (e.dataTransfer.types.includes("text/plain")) {
      e.dataTransfer.dropEffect = "move";
      currentContext.reorder(props.dataItem);
    }
  };

  /**
   * Handles onDragStart event
   * Closes popup and tracks drag state to prevent flickering
   * @param {Object} e
   * @returns {Void}
   * @author Himanshu Negi
   */
  const onDragStartHandler = (e) => {
    if (!e) return;

    // Set dragging state and close popup to prevent flickering
    setIsDragging(true);
    setPopupItem(null);
    setHoveredItem(null);
    setHoveredBookmark(null);
    clearHoverTimeout();

    currentContext.dragStart(props.dataItem);
    const dataType = props.dataItem.EntityType === label.PROJECT ? "bookmark-project" : "bookmark-task";
    e.dataTransfer.setData("text/plain", dataType);
    e.dataTransfer.effectAllowed = "move";
  };

  /**
   * Handles onDragEnd event
   * Resets drag state when drag operation completes or is cancelled
   * @param {Object} e
   * @returns {Void}
   */
  const onDragEndHandler = () => {
    setIsDragging(false);
  };

  /**
   * Handles onDrop event
   * @param {Object} e
   * @returns {Void}
   * @author Himanshu Negi
   */
  const onDropHandler = (e) => {
    if (!e) return;
    e.preventDefault();
    setIsDragging(false);
    const dataType = props.dataItem.EntityType === label.PROJECT ? "bookmark-project" : "bookmark-task";
    const draggedData = e.dataTransfer.getData("text/plain");
    if (draggedData === dataType) {
      currentContext.setActiveItem(null);
      currentContext.onDrop(e);
    }
  };

  return (
    <>
      <td>
        <div className='text-truncate' onDragOver={onDragOverHandler} onDrop={onDropHandler}>
          {props.dataItem.EntityType === label.PROJECT ? (
            <div
              id={`bookmark-project-${props.dataItem.Id}`}
              draggable={true}
              onDragStart={onDragStartHandler}
              onDragEnd={onDragEndHandler}
              className='cursor-pointer d-block'
              onClick={handleProjectBookMarkClick}>
              {bookmarkJsx()}
            </div>
          ) : (
            <div
              id={`bookmark-task-${props.dataItem.Id}`}
              className='cursor-pointer'
              draggable={true}
              onDragStart={onDragStartHandler}
              onDragEnd={onDragEndHandler}
              onClick={handleBookmarkClick}>
              {bookmarkJsx()}
            </div>
          )}
        </div>
      </td>
    </>
  );
};

export default MarkBookmark;
